//@ts-nocheck
import { stringComparison } from "../../utils/app-utils";
import {
  formatDateTimeForDisplay,
  formatEpochSecondsForDisplay,
  normalizeEpochSeconds,
  toEpochSecondsForSort,
} from "../../utils/datetime-display";

/** Normalize API userType strings (e.g. "Business Unit Admin", "enterpriseadmin"). */
function compactUserTypeKey(userType: string | undefined | null): string {
  return (userType ?? "").trim().toLowerCase().replace(/\s+/g, "");
}

/**
 * Maps user search row fields to the USER PROFILE column:
 * Destination profile → type; regular profile → admin role from userType or Participant.
 */
export function formatProfileLabel(
  userType: string | undefined | null,
  userProfileType: string | undefined | null,
): string {
  if (stringComparison(userProfileType, "destination")) {
    return "Destination";
  }
  const t = compactUserTypeKey(userType);
  if (t === "enterpriseadmin") {
    return "Enterprise Admin";
  }
  if (t === "businessadmin" || t === "businessunitadmin") {
    return "Business Unit Admin";
  }
  return "Participant";
}

/**
 * Users grid label for the Enterprise Admin business-unit sentinel.
 * Stored and filtered value stays `all` (same as Add/Edit option value).
 */
export function formatBusinessUnitForDisplay(raw: string | undefined | null): string {
  const s = raw ?? "";
  if (stringComparison(s.trim(), "all")) {
    return "All";
  }
  return s;
}

export { toEpochSecondsForSort, normalizeEpochSeconds, formatEpochSecondsForDisplay };

/** User search coerces empty last sign-on to these API strings (UserProxy.SearchUserAsync). */
const NEVER_LOGIN_SENTINEL = /^(not logged-in|not applicable)$/i;

/** Grid + detail: sort key and display label for last sign-on API values. */
export function formatLastSignOnForDisplay(raw: string | undefined | null): {
  sortEpochSec: number;
  display: string;
} {
  const s = (raw ?? "").trim();
  if (!s || NEVER_LOGIN_SENTINEL.test(s)) {
    return { sortEpochSec: 0, display: "Never" };
  }
  const sec = normalizeEpochSeconds(s);
  if (sec <= 0) {
    return { sortEpochSec: 0, display: "Never" };
  }
  return { sortEpochSec: sec, display: formatEpochSecondsForDisplay(sec) };
}

export function formatLastSignOnForGrid(
  raw: string | undefined | null,
): ReturnType<typeof formatLastSignOnForDisplay> {
  return formatLastSignOnForDisplay(raw);
}

/**
 * Plain-text body for the ONBOARDING DATE column hover tooltip (ACME-1350 / ACME-1351).
 *
 * Product split (intentional):
 * - Column header and cell value use **create** time (`createDateTime` in UsersMainView).
 * - Hover only shows **last modification** metadata from the user-search row.
 */
export function buildOnboardingHoverText(
  updateRaw: string | undefined | null,
  updatedBy: string | undefined | null,
): string {
  const parts: string[] = [];
  if (updateRaw != null && String(updateRaw).trim() !== "") {
    const sec = normalizeEpochSeconds(updateRaw);
    if (sec > 0) {
      const formatted = formatDateTimeForDisplay(sec);
      if (formatted) {
        parts.push(`Last Modified Date: ${formatted}`);
      }
    }
  }
  if (updatedBy != null && String(updatedBy).trim() !== "") {
    parts.push(`Last Modified By: ${String(updatedBy).trim()}`);
  }
  return parts.length > 0 ? parts.join("\n") : "No modification metadata available.";
}

/** Last Sign-on on Edit User (same rules as Users grid). */
export function formatUserDetailDateTime(raw: string | undefined | null): string {
  return formatLastSignOnForDisplay(raw).display;
}
